import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';

const CompanionSacrifice: React.FC = () => {
  const { 
    companions, 
    globalMultipliers,
    sacrificeCompanion,
    formatNumber
  } = useGameStore();

  const getAbilityName = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Bonus';
      case 'coinPerSecond': return 'Münzen/s';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrung';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritischer Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensraub';
      case 'armorPenetration': return 'Rüstungsdurchschlag';
      default: return 'Unbekannt';
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '%';
      case 'coinPerSecond': return '/s';
      case 'gemChance': return '%';
      case 'xpBonus': return '%';
      case 'gardenBonus': return '%';
      case 'autoClickPower': return '';
      case 'upgradeDiscount': return '%';
      case 'prestigeBonus': return '%';
      case 'criticalHit': return '%';
      case 'healing': return '';
      case 'shield': return '';
      case 'counterAttack': return '%';
      case 'lifeSteal': return '%';
      case 'armorPenetration': return '%';
      default: return '';
    }
  };
  
  const [selectedCompanion, setSelectedCompanion] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Filtere göttliche und mystisch göttliche Begleiter
  const divineCompanions = companions.discovered.filter(companion => {
    // Göttliche Begleiter haben ★ am Anfang und Ende des Namens
    const isDivine = companion.name.startsWith('★') && companion.name.endsWith('★');
    
    // Mystisch göttliche Begleiter haben ★★★ am Anfang und Ende des Namens
    const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
    
    // Zusätzliche Sicherheit: Begleiter haben mindestens 4 Fähigkeiten
    const hasEnoughAbilities = companion.abilities.length >= 4;
    
    return (isDivine || isMysticDivine) && hasEnoughAbilities;
  });

  // Berechne aktuellen Gesamt-Multiplikator
  const currentMultiplier = globalMultipliers?.totalMultiplier || 1;
  const newMultiplier = currentMultiplier * 2; // Standard für normale göttliche Begleiter

  const handleSacrifice = () => {
    if (!selectedCompanion) return;
    
    const companion = divineCompanions.find(c => c.id === selectedCompanion);
    if (!companion) return;

    // Prüfe ob es ein mystisch göttlicher Begleiter ist
    const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
    
    // Verwende den entsprechenden Multiplikator
    const multiplier = isMysticDivine ? 10 : 2;
    
    // Rufe sacrificeCompanion mit dem korrekten Multiplikator auf
    sacrificeCompanion(selectedCompanion, multiplier);
    setSelectedCompanion(null);
    setShowConfirmation(false);
  };

  const openConfirmation = (companionId: string) => {
    setSelectedCompanion(companionId);
    setShowConfirmation(true);
  };

  if (divineCompanions.length === 0) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 sm:p-6 rounded-lg border-2 border-red-200">
        <h3 className="text-lg sm:text-xl font-bold text-red-600 mb-3 sm:mb-4 flex items-center">
          ⚡ Göttliches Opfer
        </h3>
        <div className="text-center py-6 sm:py-8">
          <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">🔥</div>
          <p className="text-gray-600 mb-2 text-sm sm:text-base">Keine göttlichen Begleiter verfügbar</p>
          <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
            Erstelle göttliche Begleiter durch Fusion von 5 legendären Begleitern im Begleiter-Upgrade, 
            um sie für permanente Multiplikatoren zu opfern.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 sm:p-6 rounded-lg border-2 border-red-200">
        <h3 className="text-lg sm:text-xl font-bold text-red-600 mb-3 sm:mb-4 flex items-center flex-wrap">
          ⚡ Göttliches Opfer
          <span className="ml-2 text-xs sm:text-sm bg-red-600 text-white px-2 py-1 rounded-full">PERMANENT</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <div className="bg-white p-3 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Aktueller Multiplikator</div>
            <div className="text-xl sm:text-2xl font-bold text-red-600">×{formatNumber(currentMultiplier)}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Nach Opferung</div>
            <div className="text-xl sm:text-2xl font-bold text-orange-600">
              ×{formatNumber(selectedCompanion ? 
                (() => {
                  const companion = divineCompanions.find(c => c.id === selectedCompanion);
                  const isMysticDivine = companion?.name.startsWith('★★★') && companion?.name.endsWith('★★★');
                  return currentMultiplier * (isMysticDivine ? 10 : 2);
                })() : newMultiplier
              )}
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-red-100 to-orange-100 p-3 rounded-lg">
          <p className="text-xs sm:text-sm text-red-700 leading-relaxed">
            <strong>⚠️ Warnung:</strong> Das Opfern eines göttlichen Begleiters ist permanent und kann nicht rückgängig gemacht werden! 
            Der Begleiter wird zerstört, aber du erhältst einen permanenten Multiplikator auf ALLE Einkommen.
            <br />
            <strong>Göttliche Begleiter (★):</strong> ×2 Multiplikator
            <br />
            <strong>Mystisch Göttliche Begleiter (★★★):</strong> ×10 Multiplikator
          </p>
        </div>
      </div>

      {/* Göttliche Begleiter Liste */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 p-2">
        {divineCompanions.map((companion) => (
          <div 
            key={companion.id}
            className="relative group cursor-pointer"
          >
            {/* Moderne Glassmorphism-Karte */}
            <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/50 hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 overflow-hidden">
              {/* Glanzeffekt */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Göttlicher Glow-Effekt */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20 rounded-2xl"></div>
              
              {/* Begleiter Info */}
              <div className="relative text-center mb-4">
                <div className="text-5xl mb-3 drop-shadow-lg">
                  {companion.icon.startsWith('data:') ? (
                    <img 
                      src={companion.icon} 
                      alt={companion.name}
                      className="w-16 h-16 object-contain"
                      style={{ display: 'inline-block' }}
                    />
                  ) : (
                    companion.icon
                  )}
                </div>
                <h4 className="text-lg font-bold text-gray-800 leading-tight mb-2 break-words">{companion.name}</h4>
                <div className="inline-flex items-center gap-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                  <span>⚡</span>
                  <span>GÖTTLICH</span>
                </div>
              </div>

              {/* Fähigkeiten Grid */}
              <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 mb-4 border border-purple-100/50">
                <div className="text-xs text-purple-600 font-bold mb-3 flex items-center gap-2">
                  <span>✨</span>
                  <span>Fähigkeiten ({companion.abilities.length})</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {companion.abilities.slice(0, 4).map((ability, index) => (
                    <div key={index} className="bg-white/70 rounded-lg p-2 text-center border border-purple-200/50">
                      <div className="text-xs text-purple-700 font-medium truncate">{getAbilityName(ability.type)}</div>
                      <div className="text-sm font-bold text-purple-600">+{formatNumber(ability.value)}{getAbilitySuffix(ability.type)}</div>
                    </div>
                  ))}
                  {companion.abilities.length > 4 && (
                    <div className="col-span-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-2 text-center border border-purple-200/50">
                      <div className="text-xs text-purple-600 font-medium">
                        +{companion.abilities.length - 4} weitere Fähigkeiten
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Opfer-Button */}
              <button
                onClick={() => openConfirmation(companion.id)}
                className="relative w-full bg-gradient-to-r from-red-500 via-orange-500 to-red-600 hover:from-red-600 hover:via-orange-600 hover:to-red-700 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg min-h-[48px] touch-manipulation overflow-hidden group"
              >
                {/* Button Glanzeffekt */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                <span className="relative flex items-center justify-center gap-2">
                  <span>🔥</span>
                  <span>Für ×{(companion.name.startsWith('★★★') && companion.name.endsWith('★★★')) ? '10' : '2'} Opfern</span>
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Bestätigungs-Modal */}
      {showConfirmation && selectedCompanion && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            {(() => {
              const companion = divineCompanions.find(c => c.id === selectedCompanion);
              if (!companion) return null;
              
              return (
                <>
                  <div className="text-center mb-4 sm:mb-6">
                    <div className="text-5xl sm:text-6xl mb-3 sm:mb-4">⚡</div>
                    <h3 className="text-lg sm:text-xl font-bold text-red-600 mb-2">Göttliches Opfer</h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      Bist du sicher, dass du <strong>{companion.name}</strong> opfern möchtest?
                    </p>
                  </div>

                  <div className="bg-red-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
                    <div className="text-center">
                      <div className="text-4xl mb-2">{companion.icon}</div>
                      <div className="font-bold text-gray-800 text-sm sm:text-base">{companion.name}</div>
                      <div className="text-xs sm:text-sm text-purple-600 mb-3">{companion.abilities.length} Fähigkeiten</div>
                      
                      <div className="text-red-600 font-bold text-base sm:text-lg mb-2">
                        WIRD PERMANENT ZERSTÖRT
                      </div>
                      <div className="text-green-600 font-bold text-base sm:text-lg">
                        Erhalte: Permanenter ×{(companion.name.startsWith('★★★') && companion.name.endsWith('★★★')) ? '10' : '2'} Multiplikator
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => setShowConfirmation(false)}
                      className="py-3 px-4 bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white rounded-lg font-bold transition-colors min-h-[48px] touch-manipulation"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleSacrifice}
                      className="py-3 px-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 active:from-red-800 active:to-orange-800 text-white rounded-lg font-bold transition-all duration-300 min-h-[48px] touch-manipulation"
                    >
                      🔥 Opfern
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanionSacrifice; 